import React from "react";
import ReactDOM from "react-dom/client";
import { i18nReady } from "@/i18n";
import "@/security/axiosSecurity";   // iter82 — CSRF + auto-refresh interceptor
import "@/security/stepUpTotp";     // incident 2026-10-17 — step-up TOTP
import "@/security/axiosDedupe";     // iter284 P.B — dedupe + tiny cache on public settings
import "@/security/accessDeniedHook"; // iter283 P4 — 423 → global toast hook
import "@/security/webVitals";        // iter284 P.D — Web Vitals + JS errors
import "@/security/requestStormDetector"; // iter286 — client-side request storm classifier
import { installGlobalErrorReporter } from "@/lib/errorReporter";  // iter108 — AI Error Monitor
import "@/index.css";
import App from "@/App";

installGlobalErrorReporter();

const root = ReactDOM.createRoot(document.getElementById("root"));
// Perf — the active UI language is a lazy chunk; render once it is in
// (bounded wait) so users never see the English fallback flash.
Promise.race([i18nReady, new Promise((r) => setTimeout(r, 4000))])
  .catch(() => {})
  .then(() => {
    root.render(
      <React.StrictMode>
        <App />
      </React.StrictMode>,
    );
  });

// ─── PWA service worker registration ───────────────────────────────────
// Register in all environments so Web Push works in the preview URL too.
// The SW itself is idempotent and network-first for navigations, so it
// never serves a stale dev bundle.
//
// iter146 — emit a `japap:sw-update-available` window event whenever a new
// service worker is detected and waiting. The PwaUpdateBanner subscribes to
// it to surface a "Nouvelle version disponible" prompt to the user.
function emitSwUpdate(reg) {
  try {
    window.dispatchEvent(new CustomEvent("japap:sw-update-available", {
      detail: { registration: reg },
    }));
  } catch (_) { /* ignore — old browsers without CustomEvent */ }
}

if ("serviceWorker" in navigator) {
  window.addEventListener("load", () => {
    navigator.serviceWorker
      .register("/sw.js", { scope: "/" })
      .then((reg) => {
        if (!reg) return;

        // iter244e — AUTOMATIC PWA UPDATE.
        // iter146 — activation policy.
        // ITER286 : `activateWaitingSW` n'est plus jamais appelé
        // automatiquement. Le SKIP_WAITING est déclenché EXCLUSIVEMENT par
        // l'utilisateur via le toast "Appliquer maintenant" (voir
        // `components/PwaUpdateBanner.jsx`). Le listener
        // `controllerchange → reload()` a également été SUPPRIMÉ ci-dessous.
        // Résultat : aucun reload spontané, l'utilisateur garde toujours son
        // scroll, ses brouillons, ses filtres, ses modales.

        // Returning visitor with an SW already waiting → juste émettre
        // l'event; le toast s'affichera. Pas d'auto-activation.
        if (reg.waiting && navigator.serviceWorker.controller) {
          emitSwUpdate(reg);
        }

        // New SW found mid-session → émettre l'event; pas d'auto-activation.
        reg.addEventListener("updatefound", () => {
          const installing = reg.installing;
          if (!installing) return;
          installing.addEventListener("statechange", () => {
            if (installing.state === "installed" && navigator.serviceWorker.controller) {
              emitSwUpdate(reg);
            }
          });
        });

        // ITER286 — Polling SW update RATIONALISÉ :
        //  • Ancienne version : `setInterval(reg.update, 30s)` pendant 5 min
        //    PUIS `setInterval(reg.update, 60s)` **infini** (jamais cleared).
        //  • Nouvelle version : uniquement une check discrète toutes les
        //    10 minutes, silencieuse. Le check via `visibilitychange`
        //    ci-dessous couvre les retours d'onglet. Aucun reload derrière.
        if (reg.active) {
          setInterval(() => { reg.update().catch(() => {}); }, 10 * 60 * 1000);
        }

        // Re-check when tab returns to foreground. Reste silencieux :
        // n'entraîne AUCUN reload automatique (voir suppression du
        // controllerchange listener ci-dessous).
        document.addEventListener("visibilitychange", () => {
          if (document.visibilityState === "visible") {
            reg.update().catch(() => {});
          }
        });
      })
      .catch(() => {
        // Silent: offline mode is a nice-to-have, not a crash cause.
      });

    // ITER286 — SUPPRIMÉ : le listener `controllerchange → reload()`
    // provoquait des rechargements globaux à chaque activation de nouveau
    // SW (fréquents en preview à cause du hot reload backend). L'utilisateur
    // perdait scroll, brouillons, onglet actif, filtres. Désormais, seul un
    // clic explicite sur "Appliquer maintenant" (toast PwaUpdateBanner)
    // déclenche le reload volontaire.
  });
}
